import {
  calcAgencyMarkupAmount,
  calcEffectivePrice,
  calcGrossPrice,
  calcReferralFee
} from '@/features/line-items/line-item-form/utils/line-item-form-utils';
import { isNil } from 'lodash';
import { formatCurrency } from '@/components/tables/table-utils';
import { formatPercentage } from 'shared/src/domain/line-item/line-item-utils';
import React from 'react';
import { Calculator } from '@/icons/calculator';

type GrossPriceProps = {
  price: number | undefined;
  agency_markup_rate: number | undefined;
};

export function GrossPrice({ price, agency_markup_rate }: GrossPriceProps) {
  const agency_markup = calcAgencyMarkupAmount(price, agency_markup_rate);
  const gross_price = calcGrossPrice(price, agency_markup);
  if (isNil(gross_price) || isNil(price) || !isAboveZero(agency_markup_rate)) return null;
  return (
    <CalculatedField
      label="Gross Price"
      detail={formatCurrency(gross_price)}
      tooltip={
        <>
          Gross Price calculated as <Bold>{formatCurrency(price)}</Bold> Price × (1 +{' '}
          <Bold>{formatPercentage(agency_markup_rate)}</Bold> Agency Markup)
        </>
      }
    />
  );
}

type AgencyMarkupProps = {
  price: number | undefined;
  agency_markup_rate: number | undefined;
};

export function AgencyMarkup({ price, agency_markup_rate }: AgencyMarkupProps) {
  const value = calcAgencyMarkupAmount(price, agency_markup_rate);
  const gross_price = calcGrossPrice(price, value);
  if (isNil(value) || isNil(gross_price) || isNil(price) || !isAboveZero(agency_markup_rate))
    return null;
  return (
    <CalculatedField
      label="Agency Markup"
      detail={formatCurrency(value)}
      tooltip={
        <>
          Agency Markup calculated as <Bold>{formatCurrency(gross_price)}</Bold> Gross Price -{' '}
          <Bold>{formatCurrency(price)}</Bold> Price
        </>
      }
    />
  );
}

type EffectivePriceProps = {
  price: number | undefined;
  referral_commission_rate: number | undefined;
};

export function EffectivePrice({ price, referral_commission_rate }: EffectivePriceProps) {
  const effective_price = calcEffectivePrice(price, referral_commission_rate);
  if (isNil(effective_price) || isNil(price) || !isAboveZero(referral_commission_rate)) return null;

  return (
    <CalculatedField
      label="Effective Price"
      detail={formatCurrency(effective_price)}
      tooltip={
        <>
          Effective Price calculated as <Bold>{formatCurrency(price)}</Bold> Price × (1 -{' '}
          <Bold>{formatPercentage(referral_commission_rate)}</Bold> Referral Rate)
        </>
      }
    />
  );
}

export function ReferralFee({ price, referral_commission_rate }: EffectivePriceProps) {
  const referral_fee = calcReferralFee(price, referral_commission_rate);
  if (isNil(referral_fee) || isNil(price) || !isAboveZero(referral_commission_rate)) return null;

  return (
    <CalculatedField
      label="Referral Fee"
      detail={formatCurrency(referral_fee)}
      tooltip={
        <>
          Referral Fee calculated as <Bold>{formatCurrency(price)}</Bold> Price ×{' '}
          <Bold>{formatPercentage(referral_commission_rate)}</Bold> Referral Rate
        </>
      }
    />
  );
}

export function isAboveZero(value: number | undefined): value is number {
  return value != null && value > 0;
}

export function CalculatedField({
  label,
  detail,
  rightText,
  tooltip
}: {
  label: string;
  detail: React.ReactNode;
  rightText?: string;
  tooltip?: React.ReactNode;
}) {
  if (tooltip) {
    return (
      <div className="group mb-1.5 grow">
        <div
          className="flex items-center justify-between rounded bg-gray-50 px-3 py-2 relative z-10"
          data-testid={`calculated-field-${label.toLowerCase().replace(/\s+/g, '-')}`}>
          <div className="basis-2/3 font-semibold">{label}</div>
          <div className="flex basis-1/3 items-center justify-between">
            <div>{detail}</div>
            {rightText ? <div className="text-sm text-gray-500">{rightText}</div> : <Calculator />}
          </div>
        </div>
        <FormTooltip>{tooltip}</FormTooltip>
      </div>
    );
  }

  return (
    <div
      className="flex grow items-center justify-between rounded bg-gray-50 px-3 py-2"
      data-testid={`calculated-field-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="basis-2/3 font-semibold">{label}</div>
      <div className="flex basis-1/3 items-center justify-between">
        <div>{detail}</div>
        {rightText ? <div className="text-sm text-gray-500">{rightText}</div> : <Calculator />}
      </div>
    </div>
  );
}

function FormTooltip({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative z-0 max-h-0 overflow-hidden transition-all duration-300 ease-in-out group-hover:max-h-40 rounded-b-md -mt-1.5">
      <div className="bg-gray-200 text-gray-600 pb-1 pt-2.5 px-3 text-center">{children}</div>
    </div>
  );
}

export function Bold({ children }: { children: React.ReactNode }) {
  return <span className="font-bold">{children}</span>;
}
